import { ContentId } from 'src/utils/constants/contentId';
import { roundToPrecision } from 'src/utils/numberFormatter';
import {
  BulkEditSelectionFeeRow,
  ListingOfficePriceUpdateInput,
} from 'src/WebApiController';

export type OfficePriceRow = {
  officeId: number;
  officeName: string | null;
  sellerFee: number | null;
  /**
   * Fee bucket this row prices with: true = domestic (US, CA), false = international,
   * null = the selection has no split, so the row prices by office alone.
   */
  isDomestic: boolean | null;
};

/**
 * POS-9555: one row per office, or two when the selection has a country fee split there.
 * The rows are index-aligned with officePriceUpdates, so this order is the order the
 * price inputs bind to.
 */
export const buildOfficePriceRows = (
  selectionFeeRows: BulkEditSelectionFeeRow[] | undefined,
  getOfficeName: (id: number) => string | null
): OfficePriceRow[] =>
  (selectionFeeRows ?? [])
    .flatMap(
      ({
        officeId,
        hasDomestic,
        hasInternational,
        domesticFee,
        internationalFee,
      }) => {
        const officeName = getOfficeName(officeId);
        const rows: OfficePriceRow[] = [];

        // Without an international row there is no split, so a single bucket-less row
        // keeps the previous one-row-per-office behaviour exactly.
        if (hasDomestic || !hasInternational) {
          rows.push({
            officeId,
            officeName,
            // The fee comes from the response, which resolves it with the same rule the
            // write path uses, so the conversion cannot use a stale login-context fee.
            sellerFee: domesticFee ?? null,
            isDomestic: hasInternational ? true : null,
          });
        }
        if (hasInternational) {
          rows.push({
            officeId,
            officeName,
            sellerFee: internationalFee ?? null,
            isDomestic: false,
          });
        }

        return rows;
      }
    )
    .toSorted((a, b) =>
      (a.officeName ?? '').localeCompare(b.officeName ?? '', undefined, {
        sensitivity: 'base',
      })
    );

/** POS-9555: the label of the fee bucket a row prices with; null when there is no split. */
export const getOfficeFeeBucketContentId = (
  isDomestic: boolean | null
): ContentId | null => {
  if (isDomestic === null) return null;
  return isDomestic ? ContentId.Domestic : ContentId.International;
};

export const toOfficePriceUpdates = (
  rows: {
    officeId: number;
    /** Fee bucket of the row; null prices by office alone (no country split). */
    isDomestic?: boolean | null;
    listPrice: number | null;
    allInPrice: number | null;
  }[]
): ListingOfficePriceUpdateInput[] =>
  rows
    .filter((r) => r.listPrice != null)
    .map((r) => ({
      officeId: r.officeId,
      isDomestic: r.isDomestic ?? null,
      listPrice: r.listPrice,
      allInPrice: r.allInPrice,
    }));

export type OfficePriceMode = 'none' | 'single' | 'multi';

/**
 * none: not office-enabled (or the selection failed to load) - account-level StubHub row.
 * single: one fee row - single StubHub row using that office's fee.
 * multi: more than one fee row - several offices, or one office with a
 *   domestic/international fee split - one StubHub row per row.
 */
export const getOfficePriceMode = (
  offices: OfficePriceRow[] | null | undefined
): OfficePriceMode => {
  const count = offices?.length ?? 0;
  if (count > 1) return 'multi';
  if (count === 1) return 'single';
  return 'none';
};

/** StubHub seller fee used for the single (non per-office) StubHub row. */
export const getStubHubSellerFee = (
  offices: OfficePriceRow[] | null | undefined,
  accountSellerFee: number | null | undefined
): number => {
  const singleOfficeFee =
    offices && getOfficePriceMode(offices) === 'single'
      ? offices[0].sellerFee
      : null;
  return singleOfficeFee ?? accountSellerFee ?? 0;
};

export const buildOfficePriceFormDefaults = (
  offices: OfficePriceRow[] | null | undefined
): ListingOfficePriceUpdateInput[] | null => {
  if (!offices || getOfficePriceMode(offices) !== 'multi') return null;
  return offices.map(({ officeId, isDomestic }) => ({
    officeId,
    isDomestic,
    listPrice: null,
    allInPrice: null,
  }));
};

/**
 * More than one fee row (several offices, or one office with a fee split): other
 * marketplaces' price overrides (price inputs and the price-by-marketplace switch) are
 * locked - there is no single StubHub price to derive from, and the backend requires a
 * list price when PriceByMarketplace is set. Markup % stays editable.
 */
export const isMarketplacePriceOverrideDisabled = (
  offices: OfficePriceRow[] | null | undefined
): boolean => getOfficePriceMode(offices) === 'multi';

export const hasOfficePriceValue = (
  rows: ListingOfficePriceUpdateInput[] | null | undefined
): boolean => (rows ?? []).some((r) => r.listPrice != null);

export const formatOfficeFeePercent = (sellerFee: number | null): number =>
  roundToPrecision((sellerFee ?? 0) * 100, 2);
